import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  STUDENT_PROFILE_QUERY_KEY,
  confirmStudentPaymentPermission,
  getStudentProfile,
  studentAuthErrorMessage,
  type StudentProfile,
} from '@/auth/studentAuth';
import { UserErrorNotice } from '@/components/UserErrorNotice';
import { STUDENT_TERMS_URL } from '@/lib/legalLinks';

import {
  CARD_HOLDER_PERMISSION_TEXT,
  GUARDIAN_PURCHASE_PERMISSION_TEXT,
} from './paymentPermissionCopy';

// The one-time "I may pay" step for an independent student
// (student-self-signup-prd.md §19, D2, D3). Two boxes, never pre-ticked:
//   1. "I'm allowed to use this payment card — it is mine or the card holder has said yes"
//   2. only under 18 (`payment_permission_needs_guardian`):
//      "I have permission from a parent or guardian to buy things on Airbotix"
// Confirming calls POST /students/me/payment-permission once. The server is
// idempotent and stores the time and terms version.

const schema = z.object({
  card_holder_permission: z.literal(true, {
    errorMap: () => ({ message: 'Tick this box to continue' }),
  }),
  guardian_permission: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

export function StudentPaymentPermissionForm({
  onConfirmed,
  onCancel,
  cancelLabel = 'Not now',
}: {
  onConfirmed: (profile: StudentProfile) => void;
  /** Shown as a secondary button when given. */
  onCancel?: () => void;
  cancelLabel?: string;
}) {
  const qc = useQueryClient();
  const profile = useQuery({
    queryKey: STUDENT_PROFILE_QUERY_KEY,
    queryFn: getStudentProfile,
  });
  // Unknown age reads as "under 18", the same as the server (D2).
  const needsGuardian = profile.data?.payment_permission_needs_guardian !== false;

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      card_holder_permission: false as unknown as true,
      guardian_permission: false,
    },
  });

  const confirm = useMutation({
    mutationFn: (values: FormValues) =>
      confirmStudentPaymentPermission({
        card_holder_permission: true,
        ...(needsGuardian ? { guardian_permission: values.guardian_permission } : {}),
      }),
    onSuccess: (saved) => {
      qc.setQueryData(STUDENT_PROFILE_QUERY_KEY, saved);
      onConfirmed(saved);
    },
  });

  const submit = (values: FormValues) => {
    if (needsGuardian && values.guardian_permission !== true) {
      setError('guardian_permission', { message: 'Tick this box to continue' });
      return;
    }
    confirm.mutate(values);
  };

  if (profile.isPending) {
    return (
      <p className="lead-text" role="status" data-testid="payment-permission-loading">
        Loading…
      </p>
    );
  }

  if (profile.isError) {
    return (
      <div data-testid="payment-permission-load-error">
        <UserErrorNotice message="We could not open this step. Try again." />
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <button type="button" className="btn-pill-primary" onClick={() => void profile.refetch()}>
            Try again
          </button>
          {onCancel && (
            <button type="button" className="btn-pill-secondary" onClick={onCancel}>
              {cancelLabel}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(submit)}
      className="space-y-4"
      data-testid="payment-permission-form"
      noValidate
    >
      <p className="text-[14px] leading-relaxed text-ink-soft">
        Before your first purchase, tell us you are allowed to pay. You do this once, and it covers
        all your purchases on Airbotix.
      </p>

      <div>
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-0.5 h-5 w-5 shrink-0 accent-brand-bubblegum"
            data-testid="payment-permission-card-holder"
            {...register('card_holder_permission')}
          />
          <span className="text-[14px] leading-relaxed text-ink">
            {CARD_HOLDER_PERMISSION_TEXT}
          </span>
        </label>
        {errors.card_holder_permission && (
          <span className="field-error">{errors.card_holder_permission.message}</span>
        )}
      </div>

      {needsGuardian && (
        <div data-testid="payment-permission-guardian-row">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-0.5 h-5 w-5 shrink-0 accent-brand-bubblegum"
              data-testid="payment-permission-guardian"
              {...register('guardian_permission')}
            />
            <span className="text-[14px] leading-relaxed text-ink">
              {GUARDIAN_PURCHASE_PERMISSION_TEXT}
            </span>
          </label>
          {errors.guardian_permission && (
            <span className="field-error">{errors.guardian_permission.message}</span>
          )}
        </div>
      )}

      <p
        className="rounded-2xl bg-wash-sky px-4 py-3 text-[13px] leading-relaxed text-ink-soft"
        data-testid="payment-permission-refund-note"
      >
        {needsGuardian
          ? 'If you are under 18 and buy something without permission, you or a parent or guardian can ask us within 7 days. We refund the part you have not used, such as unused Stars or classes that have not started. '
          : 'Prices are in Australian dollars. You always see the price before you pay. '}
        <a
          href={STUDENT_TERMS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold underline"
        >
          Read the Terms
        </a>
        .
      </p>

      {confirm.isError && (
        <UserErrorNotice
          message={studentAuthErrorMessage(confirm.error, 'We could not save this. Try again.')}
        />
      )}

      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          type="submit"
          className="btn-pill-primary"
          disabled={confirm.isPending}
          data-testid="payment-permission-confirm"
        >
          {confirm.isPending ? 'Saving…' : 'Confirm and continue'}
        </button>
        {onCancel && (
          <button
            type="button"
            className="btn-pill-secondary"
            onClick={onCancel}
            disabled={confirm.isPending}
            data-testid="payment-permission-cancel"
          >
            {cancelLabel}
          </button>
        )}
      </div>
    </form>
  );
}
